import React, { createContext } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable, action, computed } from "mobx";
import extend from "lodash/extend";
import map from "lodash/map";
import includes from "lodash/includes";
import autobind from "autobind-decorator";
import classNames from "classnames";
import { TransitionMotion, spring } from "react-motion";
import enhanceWithClickOutside from "react-click-outside";

// Wraps a component to be used as a expandable menu item in the site header
//
// Note that this should not be mixed with a decorated or otherwise
// observer'd component, as doing so will cause expected observer
// functionality to be lost
const BaseHeaderContext = createContext({ selectedHeaderItem: {}, isMobileMenu: false, enableAnimations: true });
const HeaderItem = (superclass, headerItemClassNames) => enhanceWithClickOutside(observer(class extends superclass {
    @observable isHovering = false;
    @observable isPinned = false;
    @observable isJustUnpinned = false;

    static propTypes = extend({}, {
        minimizable: PropTypes.bool,
        showChildrenOnHover: PropTypes.bool,
        enableOnFocus: PropTypes.bool,
        enableOnMouseEnter: PropTypes.bool,
        enableOnMouseLeave: PropTypes.bool
    }, superclass.propTypes);

    static contextType = BaseHeaderContext;

    static defaultProps = extend({}, {
        minimizable: true,
        showChildrenOnHover: false,
        enableOnFocus: true,
        enableOnMouseEnter: true,
        enableOnMouseLeave: true
    }, superclass.defaultProps);

    @computed
    get showChildren() {
        return (this.isHovering && !this.isJustUnpinned) || this.isPinned;
    }

    @autobind
    @action
    handleClickOutside() {
        this.isPinned = false;
    }

    @autobind
    @action
    handleClick(e) {
        if (this.props.children) {
            e.preventDefault();
            if (!this.isPinned) {
                this.isPinned = true;
            } else {
                this.isPinned = false;
                this.isJustUnpinned = true;
            }
        }
    }

    @autobind
    @action
    handleFocus(e) {
        this.isHovering = true;
    }

    @autobind
    @action
    handleBlur(e) {
        this.isHovering = false;
    }

    @autobind
    @action
    handleMouseEnter(e) {
        this.isJustUnpinned = false;
        if (this.props.showChildrenOnHover) {
            if (this.hoverDelayTimeout) {
                clearTimeout(this.hoverDelayTimeout);
            }
            this.hoverDelayTimeout = setTimeout(() => {
                this.isHovering = true;
            }, 300);
        }
    }

    @autobind
    @action
    handleMouseLeave(e) {
        if (this.hoverDelayTimeout) {
            clearTimeout(this.hoverDelayTimeout);
        }
        this.hoverDelayTimeout = setTimeout(() => {
            this.isHovering = false;
        }, 300);
    }

    render() {
        const headerItemClasses = classNames(
            "header-item",
            headerItemClassNames,
            {
                selected: this.props.name && includes(this.context.selectedHeaderItem, this.props.name),
                "menu-open": this.showChildren
            });
        const showChildren = this.showChildren || this.context.isMobileMenu;
        const isMobile = !includes(headerItemClassNames, "mobile-menu-item");
        const springConfig = { stiffness: 400, damping: 30, duration: 400 };

        const {
            enableOnFocus,
            enableOnMouseEnter,
            enableOnMouseLeave
        } = this.props;

        return (
            <div className={headerItemClasses}
                onFocus={enableOnFocus ? this.handleFocus : undefined}
                onMouseEnter={enableOnMouseEnter ? this.handleMouseEnter : undefined}
                onMouseLeave={enableOnMouseLeave ? this.handleMouseLeave : undefined}
            >
                {super.render()}
                {this.props.children &&
                    <TransitionMotion
                        willEnter={() => ({ x: -100 })}
                        willLeave={() => ({ x: spring(-100, springConfig) })}
                        styles={showChildren ? [{
                            key: "menu",
                            data: this.props.children,
                            style: { x: spring(0, springConfig) }
                        }] : []}
                    >
                        {interpolatedStyles => this.context.disableTransform ?
                            (<div className="sub-item-container">
                                {map(interpolatedStyles, ({ data, key }) => {
                                    const children = data;
                                    return (
                                        <div key={key} className="sub-items">
                                            {children}
                                        </div>
                                    );
                                })}
                            </div>) :
                            (<div className="sub-item-container animated-sub-item-container">
                                {map(interpolatedStyles, ({ data, key, style }) => {
                                    const children = data;
                                    return (
                                        <div key={key} className="sub-items" style={this.context.enableAnimations && isMobile ?
                                            { transform: `translate3d(0, ${style.x}%, 0)` } :
                                            { transform: `translate3d(${style.x}%, 0, 0)` }}
                                        >
                                            {children}
                                        </div>
                                    );
                                })}
                            </div>)}
                    </TransitionMotion>
                }
            </div>
        );
    }
}));

export { BaseHeaderContext };
export default HeaderItem;
